import React from "react";
import { Ticket, MapPin, Calendar, Clock, IndianRupee, ArrowDown } from "lucide-react";
import { Reveal, Eyebrow } from "./Decorative";
import {
  poster, POSTER_ALT, fullDate, shortDate, timeLines, fromLabel, priceLabel,
  countdownLabel, useTodayIST,
} from "../lib/shows";
import { EVENT_TITLE, EVENT_FORMAT, FEATURING, PRESENTER } from "../data/shows";
import { trackTicketClick } from "../lib/track";
import { NEXT_ANCHOR } from "./NextSteps";

export const SHOWS_ANCHOR = "upcoming-shows";
export const SHOWS_HREF = `/#${SHOWS_ANCHOR}`;

const LANDSCAPE = poster("bhaav-sangam-landscape");
const STORY = poster("bhaav-sangam-story");

const lqip = (p) => ({
  backgroundImage: `url(${p.lqip})`, backgroundSize: "cover", backgroundPosition: "center",
});

/**
 * One direct-to-BookMyShow button per city: "Book Delhi", then the date, the
 * countdown and the starting price. Used wherever the home page asks for the
 * booking (the hero strip, the mid-page and final calls to action), so a
 * visitor is always one tap from the right BookMyShow page.
 */
export function CityBookButtons({ shows, placement, className = "" }) {
  const today = useTodayIST();
  return (
    <div
      className={`grid gap-3 md:gap-5 ${shows.length > 1 ? "grid-cols-2" : "grid-cols-1"} ${className}`}
      data-testid={`${placement}-book-buttons`}
    >
      {shows.map((s) => {
        const price = priceLabel(s);
        return (
          <a
            key={s.id}
            href={s.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackTicketClick(s, placement)}
            className="city-book-btn"
            data-testid={`${placement}-book-${s.id}`}
          >
            <span className="flex items-center justify-center gap-1.5 whitespace-nowrap font-semibold text-[14px] min-[400px]:text-[15px] md:text-lg leading-tight">
              <Ticket size={17} strokeWidth={1.8} className="flex-shrink-0" />
              Book {s.city}
            </span>
            <span className="mt-1 block text-[12px] md:text-sm leading-snug opacity-90">
              {shortDate(s)} · {countdownLabel(s, today)}
            </span>
            {price && (
              <span className="block text-[12px] md:text-sm leading-snug font-semibold">from {price}</span>
            )}
          </a>
        );
      })}
    </div>
  );
}

/**
 * Hero, art-directed poster: the 9:16 story cut below 768px, the 2:1
 * landscape above. The booking strip sits ABOVE the poster, straight under the
 * nav: ad traffic lands here on a phone, where the story poster alone is a
 * full screen tall, so buttons under it were below the fold. The poster still
 * carries the title, the dates and the venues, so nothing is laid over it.
 * The poster is the LCP element, hence eager + high priority.
 */
export function ShowsHero({ shows, soldOut = false }) {
  return (
    <section
      className="relative w-full bg-[#0F0A07] pt-16 md:pt-[72px] pb-12 md:pb-16"
      data-testid="shows-hero"
    >
      {/* The page's one H1. The poster carries the visible title as artwork,
          which search engines cannot read, so the heading is spelled out for
          them and for screen readers. */}
      <h1 className="sr-only">Antara — Live Bhajan Jamming with Anshu Goyal, by Kashi with Anshu</h1>
      {soldOut ? (
        // Sold out: the strip that held the Book buttons says so, and points
        // down to what a visitor can still do. It is the first thing on a phone.
        <div className="px-5 pt-5 pb-6 md:pt-7 md:pb-9 text-center" data-testid="hero-sold-out">
          <p className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 font-body text-xs font-semibold tracking-[0.18em] uppercase sold-out-badge">
            Sold out
          </p>
          <p className="mt-3 mx-auto max-w-xl font-display text-2xl md:text-3xl leading-snug" style={{ color: "#FBF6EE" }}>
            {soldOutLine(shows)}
          </p>
          <a
            href={`#${NEXT_ANCHOR}`}
            className="mt-3 inline-flex items-center gap-1.5 font-body text-sm md:text-base underline underline-offset-4 hover:opacity-80 transition"
            style={{ color: "#E5C880" }}
            data-testid="hero-next-link"
          >
            Bring Antara to your city, your event or your brand <ArrowDown size={15} />
          </a>
        </div>
      ) : (
        <div className="px-4 pt-4 pb-5 md:pt-6 md:pb-8" data-testid="hero-book-strip">
          <CityBookButtons shows={shows} placement="hero" className="mx-auto max-w-[480px] md:max-w-3xl" />
          <p className="mt-2.5 text-center font-body text-[11px] md:text-xs tracking-wide" style={{ color: "rgba(232,217,197,0.75)" }}>
            Booking on BookMyShow
          </p>
        </div>
      )}

      <div className="mx-auto w-full max-w-[480px] md:max-w-[1400px] md:px-6">
        <picture>
          <source media="(max-width: 767px)" type="image/avif" srcSet={STORY.avif} sizes="(max-width: 480px) 100vw, 480px" width={STORY.width} height={STORY.height} />
          <source media="(max-width: 767px)" type="image/webp" srcSet={STORY.webp} sizes="(max-width: 480px) 100vw, 480px" width={STORY.width} height={STORY.height} />
          <source media="(max-width: 767px)" type="image/jpeg" srcSet={STORY.jpg} sizes="(max-width: 480px) 100vw, 480px" width={STORY.width} height={STORY.height} />
          <source type="image/avif" srcSet={LANDSCAPE.avif} sizes="(min-width: 1400px) 1352px, 96vw" width={LANDSCAPE.width} height={LANDSCAPE.height} />
          <source type="image/webp" srcSet={LANDSCAPE.webp} sizes="(min-width: 1400px) 1352px, 96vw" width={LANDSCAPE.width} height={LANDSCAPE.height} />
          <img
            src={LANDSCAPE.src}
            srcSet={LANDSCAPE.jpg}
            sizes="(min-width: 1400px) 1352px, 96vw"
            width={LANDSCAPE.width}
            height={LANDSCAPE.height}
            alt={POSTER_ALT}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="block w-full h-auto md:rounded-[14px]"
            style={lqip(LANDSCAPE)}
            data-testid="shows-hero-poster"
          />
        </picture>
      </div>
    </section>
  );
}

/** "Delhi and Chandigarh, both sold out. Thank you for filling the room." */
export function soldOutLine(shows) {
  const cities = shows.map((s) => s.city);
  const list = cities.length > 1 ? `${cities.slice(0, -1).join(", ")} and ${cities[cities.length - 1]}` : cities[0];
  return `${list}${cities.length > 1 ? ", both" : ""} sold out. Thank you for filling the room.`;
}

export function ShowCard({ show, placement = "card" }) {
  const times = timeLines(show);
  const today = useTodayIST();
  const from = fromLabel(show);
  return (
    <article
      className="rounded-2xl border p-6 md:p-7"
      style={{ background: "#1C1A17", borderColor: "rgba(201,169,97,0.3)" }}
      data-testid={`show-card-${show.id}`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-display text-3xl md:text-4xl" style={{ color: "#FBF6EE" }}>{show.city}</h3>
        {show.soldOut ? (
          <span
            className="flex-shrink-0 rounded-full px-3 py-1 font-body text-xs font-semibold tracking-[0.14em] uppercase sold-out-badge"
            data-testid={`show-sold-out-${show.id}`}
          >
            Sold out
          </span>
        ) : (
          <span
            className="flex-shrink-0 rounded-full px-3 py-1 font-body text-xs font-semibold tracking-wide"
            style={{ background: "rgba(201,169,97,0.18)", color: "#E5C880" }}
            data-testid={`show-countdown-${show.id}`}
          >
            {countdownLabel(show, today)}
          </span>
        )}
      </div>
      <ul className="mt-4 space-y-2.5 font-body text-[15px]" style={{ color: "#E8D9C5" }}>
        <li className="flex gap-3">
          <Calendar size={17} strokeWidth={1.4} className="mt-0.5 flex-shrink-0" style={{ color: "#C9A961" }} />
          <span>{fullDate(show)}</span>
        </li>
        <li className="flex gap-3">
          <MapPin size={17} strokeWidth={1.4} className="mt-0.5 flex-shrink-0" style={{ color: "#C9A961" }} />
          <span>{show.venue}</span>
        </li>
        {times.length > 0 && (
          <li className="flex gap-3">
            <Clock size={17} strokeWidth={1.4} className="mt-0.5 flex-shrink-0" style={{ color: "#C9A961" }} />
            <span>{times.join(" · ")}</span>
          </li>
        )}
        {from && !show.soldOut && (
          <li className="flex gap-3" data-testid={`show-price-${show.id}`}>
            <IndianRupee size={17} strokeWidth={1.4} className="mt-0.5 flex-shrink-0" style={{ color: "#C9A961" }} />
            <span className="font-semibold" style={{ color: "#FBF6EE" }}>{from}</span>
          </li>
        )}
      </ul>
      {show.soldOut ? (
        // Not a link and not a button: there is nothing left to book. Dashed,
        // muted and without the gold fill so it never reads as tappable.
        <p className="sold-out-slot mt-6" data-testid={`show-sold-out-slot-${show.id}`}>
          Sold out · Thank you, {show.city}
        </p>
      ) : (
        <a
          href={show.bookingUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackTicketClick(show, placement)}
          className="cta-primary mt-6 w-full justify-center"
          style={{ padding: "15px 28px", fontSize: 16 }}
          data-testid={`show-book-${show.id}`}
        >
          <Ticket size={18} strokeWidth={1.6} />
          <span>Book tickets</span>
          <span className="cta-arrow">→</span>
        </a>
      )}
    </article>
  );
}

/**
 * "Upcoming Shows": one card per show, side by side from tablet width up.
 * No poster here: the hero already carries it, and a second copy straight
 * after it read as repetition. The anchor target for every "Book tickets"
 * link on the site.
 */
export function UpcomingShows({ shows }) {
  return (
    <section
      id={SHOWS_ANCHOR}
      className="scroll-mt-20 bg-[#0F0A07] py-20 md:py-28 px-6"
      data-testid="upcoming-shows"
    >
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <div className="text-center">
            <Eyebrow>{shows.every((x) => x.soldOut) ? "Sold out" : "Upcoming shows"}</Eyebrow>
            <h2 className="mt-4 font-display text-4xl md:text-5xl leading-[1.08]" style={{ color: "#FBF6EE" }}>
              {PRESENTER} presents <span className="gold-grad italic">{EVENT_TITLE}</span>
            </h2>
            <p className="mt-4 font-body text-base md:text-lg" style={{ color: "#E8D9C5" }}>
              {EVENT_FORMAT} featuring {FEATURING}
            </p>
          </div>
        </Reveal>

        <div className="mt-12 md:mt-16 mx-auto max-w-4xl grid md:grid-cols-2 gap-6 items-start">
          {shows.map((s) => <ShowCard key={s.id} show={s} />)}
        </div>
      </div>
    </section>
  );
}
